import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Send, Copy, Users, GraduationCap, Trash2, RefreshCcw, Link2 } from "lucide-react";
import { shortDate } from "@/lib/format";

type Invite = {
  id: string; school_id: string; role: "parent" | "student"; full_name: string;
  email: string | null; phone: string | null; student_id: string | null;
  class_label: string | null; invite_code: string; status: string;
  accepted_at: string | null; expires_at: string; created_at: string;
  max_uses: number; uses_count: number; revoked_at: string | null;
};

const genCode = () => {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let out = ""; for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

export function InvitationsPanel({ schoolId, userId }: { schoolId: string; userId: string }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<"parent" | "student">("parent");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", class_label: "P4 · Day", student_id: "", max_uses: 1, expires_days: 30 });

  const { data: invites = [], isLoading } = useQuery({
    queryKey: ["invitations", schoolId],
    queryFn: async () => (await supabase.from("school_invitations" as never).select("*").eq("school_id", schoolId).order("created_at", { ascending: false })).data as unknown as Invite[] ?? [],
  });

  const { data: students = [] } = useQuery({
    queryKey: ["invite-students", schoolId],
    queryFn: async () => (await supabase.from("students").select("id,full_name,class_label,guardian_id").eq("school_id", schoolId).order("full_name")).data ?? [],
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!form.full_name.trim()) throw new Error("Name is required");
      if (!form.email && !form.phone) throw new Error("Provide email or phone");
      const code = genCode();
      const { error } = await supabase.from("school_invitations" as never).insert({
        school_id: schoolId, invited_by: userId, role,
        full_name: form.full_name.trim(),
        email: form.email || null, phone: form.phone || null,
        class_label: role === "student" ? form.class_label : null,
        student_id: form.student_id || null,
        invite_code: code,
        max_uses: Math.max(1, form.max_uses),
        expires_at: new Date(Date.now() + form.expires_days * 86400_000).toISOString(),
      } as never);
      if (error) throw error;
      return code;
    },
    onSuccess: (code) => {
      toast.success(`Invite ${code} created`);
      setForm({ full_name: "", email: "", phone: "", class_label: "P4 · Day", student_id: "", max_uses: 1, expires_days: 30 });
      qc.invalidateQueries({ queryKey: ["invitations", schoolId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.rpc as unknown as (fn: string, a: Record<string, unknown>) => Promise<{ error: { message: string } | null }>)("revoke_school_invitation", { _id: id });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { toast.success("Revoked · inviter notified"); qc.invalidateQueries({ queryKey: ["invitations", schoolId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const resend = useMutation({
    mutationFn: async (inv: Invite) => {
      const code = genCode();
      const { error } = await supabase.from("school_invitations" as never).update({
        invite_code: code, status: "pending", uses_count: 0,
        expires_at: new Date(Date.now() + 30 * 86400_000).toISOString(),
      } as never).eq("id", inv.id);
      if (error) throw error;
      return code;
    },
    onSuccess: (code) => { toast.success(`New code ${code}`); qc.invalidateQueries({ queryKey: ["invitations", schoolId] }); },
  });

  const inviteLink = (code: string) => `${window.location.origin}/auth?invite=${code}`;

  const pending = invites.filter((i) => i.status === "pending");
  const accepted = invites.filter((i) => i.status === "accepted");

  return (
    <div className="space-y-5">
      <Card className="p-6 bg-gradient-to-br from-primary/5 via-card to-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Badge variant="secondary" className="gap-1"><UserPlus className="h-3 w-3" />Invitations</Badge>
            <h2 className="mt-2 font-display text-2xl">Invite parents & learners</h2>
            <p className="text-sm text-muted-foreground">Anyone who accepts is auto-linked to this school. Codes expire after 30 days.</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Pending / Accepted</div>
            <div className="font-display text-2xl">{pending.length} / {accepted.length}</div>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="grid gap-3 md:grid-cols-5">
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as "parent" | "student")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">Parent / Guardian</SelectItem>
                <SelectItem value="student">Learner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Amina Namukasa" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="a@x.com" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+256 7xx" /></div>
          {role === "student" && (
            <div><Label>Class</Label><Input value={form.class_label} onChange={(e) => setForm({ ...form, class_label: e.target.value })} /></div>
          )}
          <div><Label>Max uses</Label><Input type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Math.max(1, parseInt(e.target.value||"1")) })} /></div>
          <div><Label>Expires in (days)</Label><Input type="number" min={1} value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: Math.max(1, parseInt(e.target.value||"30")) })} /></div>
          {role === "parent" && (
            <div className="md:col-span-2">
              <Label>Link to existing student (optional)</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {students.map((s: { id: string; full_name: string; class_label: string | null }) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.class_label ?? ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button onClick={() => create.mutate()} disabled={create.isPending} className="gap-2">
            <Send className="h-4 w-4" />{create.isPending ? "Creating…" : "Create invitation"}
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg">Active invitations</h3>
          <span className="text-xs text-muted-foreground">{invites.length} total</span>
        </div>
        <div className="overflow-hidden rounded-lg border">
          <div className="grid grid-cols-[80px_1fr_120px_120px_120px_140px_180px] gap-2 border-b bg-muted/40 px-3 py-2 text-[11px] uppercase text-muted-foreground">
            <div>Role</div><div>Person</div><div>Class</div><div>Uses</div><div>Status</div><div>Expires</div><div>Code / link</div>
          </div>
          {isLoading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && invites.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No invitations yet. Create one above.</div>}
          {invites.map((inv) => (
            <div key={inv.id} className="grid grid-cols-[80px_1fr_120px_120px_120px_140px_180px] gap-2 border-b px-3 py-2 items-center text-sm">
              <div>{inv.role === "parent" ? <Badge variant="secondary" className="gap-1"><Users className="h-3 w-3" />Parent</Badge> : <Badge variant="secondary" className="gap-1"><GraduationCap className="h-3 w-3" />Learner</Badge>}</div>
              <div>
                <div className="font-medium">{inv.full_name}</div>
                <div className="text-xs text-muted-foreground">{inv.email ?? inv.phone ?? "—"}</div>
              </div>
              <div className="text-xs">{inv.class_label ?? "—"}</div>
              <div className="text-xs font-mono">{inv.uses_count}/{inv.max_uses}</div>
              <div>
                <Badge variant={inv.status === "accepted" ? "default" : inv.status === "pending" ? "secondary" : "outline"}>{inv.status}</Badge>
              </div>
              <div className="text-xs">{shortDate(inv.expires_at)}</div>
              <div className="flex items-center gap-1">
                <span className="font-mono text-xs">{inv.invite_code}</span>
                <Button size="sm" variant="ghost" title="Copy code" onClick={() => { navigator.clipboard.writeText(inv.invite_code); toast.success("Code copied"); }}>
                  <Copy className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" title="Copy link" onClick={() => { navigator.clipboard.writeText(inviteLink(inv.invite_code)); toast.success("Link copied"); }}>
                  <Link2 className="h-3 w-3" />
                </Button>
                {inv.status === "pending" && (
                  <>
                    <Button size="sm" variant="ghost" title="Regenerate" onClick={() => resend.mutate(inv)}><RefreshCcw className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" title="Revoke" onClick={() => revoke.mutate(inv.id)}><Trash2 className="h-3 w-3" /></Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/** Simple redeem card for parents/students landing with ?invite=CODE */
export function RedeemInviteCard({ code, onRedeemed }: { code: string; onRedeemed: () => void }) {
  const [status, setStatus] = useState<"idle" | "working" | "done" | "error">("idle");
  const [msg, setMsg] = useState<string>("");
  const redeem = async () => {
    setStatus("working");
    const { data, error } = await (supabase.rpc as unknown as (fn: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)(
      "redeem_school_invitation", { _code: code });
    if (error) { setStatus("error"); setMsg(error.message); return; }
    setStatus("done"); setMsg(JSON.stringify(data));
    onRedeemed();
  };
  return (
    <Card className="p-5 border-dashed">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Badge variant="secondary" className="gap-1"><Link2 className="h-3 w-3" />Invitation</Badge>
          <div className="mt-2 font-display text-xl">You've been invited to a school</div>
          <div className="text-sm text-muted-foreground">Code: <span className="font-mono">{code}</span></div>
          {msg && <div className="text-xs mt-1 text-muted-foreground break-all">{msg}</div>}
        </div>
        <Button onClick={redeem} disabled={status === "working" || status === "done"}>{status === "done" ? "Linked" : status === "working" ? "Linking…" : "Accept & link"}</Button>
      </div>
    </Card>
  );
}
